import { ChangeEvent, FC, useMemo, useState } from "react";
import { Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import { MiSearchInput } from "@miplatform/components";
import { FONT_WEIGHTS } from "@miplatform/constants";
import { ITransferListGroup, ITransferListItem } from "@miplatform/types";
import { groupBy } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import {
  ITransferListItemsViewProps,
  ITransferListProps,
} from "../TransferList.props";
import { EmptyTransferListView } from "./EmptyTransferListView";
import { TransferListItem } from "./TransferListItem";
import { TransferListLayout } from "./TransferListLayout";

export const TransferList: FC<ITransferListProps> = ({
  availableItems,
  selectedItems,
  onAddItem,
  onRemoveItem,
  availableTitle = "Available",
  selectedTitle = "Selected",
  renderSelectedItemsCount,
  renderAvailableHeaderComponent,
  renderSelectedHeaderComponent,
  renderItemTitle,
  availableSearchPlaceholder = "Search",
  selectedSearchPlaceholder = "Search",
  showAvailableItemsSearch,
  showSelectedItemsSearch,
  allowResorting,
  onSelectedListReorder,
  EmptyAvailableList,
  EmptySelectedList,
  emptyAvailableListMessage = "There are no available items to choose from.",
  emptySelectedListMessage = "Selected items will show here.",
  groupBy,
  groupByMapper,
  getGroupByInfo,
  disabled,
  customAction,
  addDisabled,
}) => {
  return (
    <TransferListLayout
      availableItems={
        <TransferListItemsView
          items={availableItems}
          onAddItem={disabled ? null : onAddItem}
          showSearch={showAvailableItemsSearch}
          searchPlaceholder={availableSearchPlaceholder}
          excludedItems={selectedItems}
          emptyMessage={emptyAvailableListMessage}
          emptyListComponent={EmptyAvailableList}
          groupBy={groupBy}
          groupByMapper={groupByMapper}
          getGroupByInfo={getGroupByInfo}
          renderItemTitle={renderItemTitle}
          addDisabled={addDisabled}
        />
      }
      selectedItems={
        allowResorting ? (
          <TransferListItemsView
            items={selectedItems}
            onRemoveItem={disabled ? null : onRemoveItem}
            showSearch={showSelectedItemsSearch}
            searchDisabled={disabled}
            allowResorting={allowResorting}
            onSelectedListReorder={onSelectedListReorder}
            emptyMessage={emptySelectedListMessage}
            emptyListComponent={EmptySelectedList}
            renderItemTitle={renderItemTitle}
            searchPlaceholder={selectedSearchPlaceholder}
          />
        ) : (
          <TransferListItemsView
            items={selectedItems}
            onRemoveItem={disabled ? null : onRemoveItem}
            showSearch={showSelectedItemsSearch}
            searchDisabled={disabled}
            emptyMessage={emptySelectedListMessage}
            emptyListComponent={EmptySelectedList}
            groupBy={groupBy}
            groupByMapper={groupByMapper}
            getGroupByInfo={getGroupByInfo}
            renderItemTitle={renderItemTitle}
            searchPlaceholder={selectedSearchPlaceholder}
            customAction={customAction}
          />
        )
      }
      availableTitle={availableTitle}
      selectedTitle={selectedTitle}
      renderAvailableHeaderComponent={renderAvailableHeaderComponent}
      renderSelectedHeaderComponent={renderSelectedHeaderComponent}
      renderSelectedItemsCount={renderSelectedItemsCount}
    />
  );
};

const TransferListItemsView: FC<ITransferListItemsViewProps> = ({
  items,
  excludedItems,
  onAddItem,
  onRemoveItem,
  showSearch,
  searchPlaceholder = "Search...",
  allowResorting,
  onSelectedListReorder,
  emptyMessage,
  emptyListComponent,
  groupBy: groupByProperty,
  groupByMapper = groupBy,
  getGroupByInfo,
  renderItemTitle,
  customAction,
  searchDisabled,
  addDisabled,
}) => {
  const { classes, theme } = useStyles();
  const [search, setSearch] = useState("");

  const handleReorder = (newItems: ITransferListItem[]) => {
    if (onSelectedListReorder) {
      onSelectedListReorder(newItems);
    }
  };

  const handleReorderUp = (itemKey: string) => {
    const index = items.findIndex((item) => item.key === itemKey);
    if (index !== -1 && index > 0) {
      const newItems = [...items];
      [newItems[index - 1], newItems[index]] = [
        newItems[index],
        newItems[index - 1],
      ];
      handleReorder(newItems);
    }
  };

  const handleReorderDown = (itemKey: string) => {
    const index = items.findIndex((item) => item.key === itemKey);
    if (index !== -1 && index < items.length - 1) {
      const newItems = [...items];
      [newItems[index + 1], newItems[index]] = [
        newItems[index],
        newItems[index + 1],
      ];
      handleReorder(newItems);
    }
  };

  const listItems = useMemo(() => {
    const filteredItems = items.filter((item) => {
      if (!item.key) {
        // prevent items with no key from being shown
        return false;
      }

      if (excludedItems?.some((excluded) => excluded.key === item.key)) {
        return false;
      }

      if (!search || search.trim() === "") {
        return true;
      }

      const searchLower = search.toLowerCase();
      const { title, description, key } = item;
      return [title, description, key].some((field) =>
        field?.toLowerCase().includes(searchLower)
      );
    });

    if (groupByProperty) {
      const groupedItems: { [key: string]: ITransferListItem[] } =
        groupByMapper(filteredItems, groupByProperty);

      const groupedList = Object.entries(groupedItems).map(
        ([groupKey, items]) => ({
          ...getGroupByInfo?.(groupKey),
          items,
        })
      );

      return groupedList.sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "")
      );
    }
    return filteredItems;
  }, [
    items,
    excludedItems,
    search,
    groupByProperty,
    groupByMapper,
    getGroupByInfo,
  ]);

  return (
    <Box
      className={classes.transferListItemsViewContainer}
      pl={groupByProperty ? theme.spacing(2) : 0}
    >
      {showSearch && (
        <MiSearchInput
          value={search}
          handleChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearch(e.target.value)
          }
          placeholder={searchPlaceholder}
          style={{
            marginLeft: 0,
            marginBottom: 2,
          }}
          disabled={searchDisabled}
        />
      )}
      {listItems.length === 0 && emptyListComponent && emptyListComponent}
      {listItems.length === 0 && !emptyListComponent && (
        <EmptyTransferListView message={emptyMessage} />
      )}
      {listItems?.length > 0 && groupByProperty ? (
        (listItems as ITransferListGroup[]).map((group, groupIndex) => {
          return (
            <Box key={group.key || groupIndex}>
              <Box>
                {group.name && (
                  <Typography variant="HD1">
                    {group.name}
                    {group.description && (
                      <Typography
                        variant="HD1"
                        fontWeight={FONT_WEIGHTS.REGULAR}
                        color={theme.palette.grey.medium}
                        component="span"
                      >
                        {` ${group.description}`}
                      </Typography>
                    )}
                  </Typography>
                )}
              </Box>
              <Box className={classes.transferGroupContainer}>
                {group.items?.map((item: ITransferListItem, index: number) => (
                  <TransferListItem
                    key={item.key}
                    item={item}
                    onAddItem={onAddItem}
                    onRemoveItem={onRemoveItem}
                    itemCount={items.length}
                    index={index}
                    renderItemTitle={renderItemTitle}
                  />
                ))}
              </Box>
            </Box>
          );
        })
      ) : (
        <Flipper
          flipKey={listItems.map((item) => item.key).join("-")}
          className={classes.transferListItemContainer}
        >
          {(listItems as ITransferListItem[]).map((item, index) => (
            <TransferListItem
              key={item.key}
              item={item}
              renderItemTitle={renderItemTitle}
              onAddItem={onAddItem}
              onRemoveItem={onRemoveItem}
              allowResorting={allowResorting}
              onReorderUp={handleReorderUp}
              onReorderDown={handleReorderDown}
              itemCount={items.length}
              index={index}
              customAction={customAction}
              addDisabled={addDisabled}
            />
          ))}
        </Flipper>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  transferListItemsViewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: 1,
  },
  transferGroupContainer: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    gap: theme.spacing(1),
  },
  transferListItemContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));
